import { z } from 'zod';

/**
 * Company funnel measured in GA4 through GTM (21/09).
 *
 * The event NAMES are the contract with the GTM container (GTM-W247XMFM): its
 * triggers match these strings literally, so renaming one here silently stops
 * the tag that listens for it. `sign_up` is GA4's own recommended event; the rest
 * follow the same snake_case the public website already pushes
 * (`request_talent_submit_success`, `candidate_access_start`…).
 */
export const ANALYTICS_EVENTS = {
	signUp: 'sign_up',
	firstLogin: 'first_login',
	accountActivated: 'account_activated',
	companyProfileCompleted: 'company_profile_completed',
	/** Same name the website pushes on every page, so both sides read alike in GTM. */
	pageView: 'kairos_page_view',
} as const;

/**
 * The three milestones the SERVER decides, as opposed to `sign_up`, which the
 * signup response already proves on its own.
 *
 * They live server-side because each one has to happen exactly once per account
 * or company whatever the browser does — reloads, retries, a second device — and
 * because one of them is not even the company's doing: `account_activated` is
 * Kairos approving the company in Scoring, from the backoffice, with nobody from
 * the company on the page.
 */
export const ANALYTICS_MILESTONES = [
	'first_login',
	'account_activated',
	'company_profile_completed',
	/**
	 * Candidate accounts only. Recorded when the server creates the login
	 * (`provisionCandidate`), delivered once on that person's next session.
	 * The company `sign_up` is not one of these: the signup screen pushes it
	 * itself, on the 2xx that created the company.
	 */
	'sign_up',
] as const;
export type AnalyticsMilestoneKind = (typeof ANALYTICS_MILESTONES)[number];

export const AnalyticsMilestoneSchema = z.object({
	kind: z.enum(ANALYTICS_MILESTONES),
	/** When it actually happened, which is not when it reaches the browser. */
	occurredAt: z.string(),
	/** Non-personal context for the GA4 event (e.g. which approval state). */
	params: z.record(z.string(), z.string()),
});
export type AnalyticsMilestoneDTO = z.infer<typeof AnalyticsMilestoneSchema>;

export const ClaimedMilestonesSchema = z.object({ milestones: z.array(AnalyticsMilestoneSchema) });
export type ClaimedMilestonesDTO = z.infer<typeof ClaimedMilestonesSchema>;

/**
 * What makes a company profile "complete" for `company_profile_completed`.
 *
 * The app had no such rule — the onboarding wizard calls itself "done" when there
 * is a SUBSCRIPTION, which is a payment milestone, not a profile one. The ruling
 * (21/09) is the company data of the wizard's first step, WITHOUT the two
 * documents: name, city, property type and trade licence number. The public
 * signup never asks for the licence, so a company cannot come out of the signup
 * already "complete" and fire this step on the same breath as `sign_up`.
 */
export const COMPANY_PROFILE_REQUIRED_FIELDS = ['companyName', 'city', 'propertyType', 'tradeLicenseNumber'] as const;

export function isCompanyProfileComplete(
	company: Partial<Record<(typeof COMPANY_PROFILE_REQUIRED_FIELDS)[number], string | null>>,
): boolean {
	return COMPANY_PROFILE_REQUIRED_FIELDS.every((field) => (company[field] ?? '').trim().length > 0);
}
